import { DeviceCommand } from "lib/deviceDrivers/sessionFramework/device/types";
import {
  buildSessionNameCommand,
  buildSelectSessionCommand,
  getNextFieldCodeCommand,
  getNextRecordCommand,
  getNextSessionIdCommand,
  getSessionCountCommand,
  resetSessionRecordCursorCommand,
  selectSessionDataModeCommand,
} from "./commands";
import {
  extractEidFromRecord,
  extractScanTime,
} from "lib/deviceDrivers/truTest/scp/device/eid";
import {
  EIDOutputFormat,
  FrameType,
  type ScpFrame,
} from "lib/deviceDrivers/truTest/scp/device/types";
import {
  type DownloadState,
  DownloadStep,
  type ImportedScan,
  type SavedSession,
  type Xrs2iEvent,
  Xrs2iEventType,
} from "./types";

export enum DownloadMachineResultType {
  CONTINUE = 0,
  COMPLETE = 1,
}

export type DownloadMachineResult =
  | {
      type: DownloadMachineResultType.CONTINUE;
      commands: DeviceCommand[];
      events: Xrs2iEvent[];
    }
  | {
      type: DownloadMachineResultType.COMPLETE;
      success: boolean;
      events: Xrs2iEvent[];
    };

/**
 * Creates a fresh Scanner Session Import state with no known sessions or progress.
 * The reducer starts here so every download run builds its own ordered session list from scratch.
 */
export function createDownloadState(): DownloadState {
  return {
    step: DownloadStep.WAITING_FOR_INITIAL_RECORD_RESET,
    totalSessionCount: null,
    sessionIds: [],
    currentSessionIndex: 0,
    completedSessionCount: 0,
    sessionsById: {},
  };
}

/**
 * Reassembles imported scans into the shape the Redux layer stores per saved session.
 * Each session keeps its scans under a readable label so progress and completion events can share the same structure.
 */
export function buildImportedScans(
  downloadState: DownloadState,
): Record<string, ImportedScan[]> {
  return downloadState.sessionIds.reduce<Record<string, ImportedScan[]>>(
    (accumulator, sessionId) => {
      const session = downloadState.sessionsById[sessionId];
      accumulator[getSessionLabel(sessionId, session?.sessionName)] =
        session?.scans ?? [];
      return accumulator;
    },
    {},
  );
}

/**
 * Advances the Scanner Session Import one frame at a time.
 * Each step either emits commands for the next scanner prompt or returns a completion result once the current session is done.
 */
export function reduceDownloadFrame(
  downloadState: DownloadState,
  frame: ScpFrame,
  eidOutputFormat: EIDOutputFormat | undefined,
): DownloadMachineResult {
  const events: Xrs2iEvent[] = [];
  const commands: DeviceCommand[] = [];

  switch (downloadState.step) {
    case DownloadStep.WAITING_FOR_INITIAL_RECORD_RESET:
      return handleWaitingForInitialRecordReset(
        downloadState,
        frame,
        events,
        commands,
      );

    case DownloadStep.WAITING_FOR_SESSION_DATA_MODE:
      return handleWaitingForSessionDataMode(
        downloadState,
        frame,
        events,
        commands,
      );

    case DownloadStep.WAITING_FOR_SESSION_COUNT:
      return handleWaitingForSessionCount(
        downloadState,
        frame,
        events,
        commands,
      );

    case DownloadStep.WAITING_FOR_SESSION_ID:
      return handleWaitingForSessionId(downloadState, frame, events, commands);

    case DownloadStep.WAITING_FOR_SESSION_SELECTION:
      return handleWaitingForSessionSelection(
        downloadState,
        frame,
        events,
        commands,
      );

    case DownloadStep.WAITING_FOR_SESSION_NAME:
      return handleWaitingForSessionName(
        downloadState,
        frame,
        events,
        commands,
      );

    case DownloadStep.WAITING_FOR_SESSION_RECORD_RESET:
      return handleWaitingForSessionRecordReset(
        downloadState,
        frame,
        events,
        commands,
      );

    case DownloadStep.WAITING_FOR_FIELD_CODE:
      return handleWaitingForFieldCode(downloadState, frame, events, commands);

    case DownloadStep.WAITING_FOR_RECORD:
      return handleWaitingForRecord(
        downloadState,
        frame,
        eidOutputFormat,
        events,
        commands,
      );

    default:
      return {
        type: DownloadMachineResultType.COMPLETE,
        success: false,
        events,
      };
  }
}

function handleWaitingForInitialRecordReset(
  downloadState: DownloadState,
  frame: ScpFrame,
  events: Xrs2iEvent[],
  commands: DeviceCommand[],
): DownloadMachineResult {
  if (frame.type !== FrameType.ACK) {
    return { type: DownloadMachineResultType.CONTINUE, commands, events };
  }

  // Move into session-mode selection only after the scanner confirms the record cursor reset.
  downloadState.step = DownloadStep.WAITING_FOR_SESSION_DATA_MODE;
  commands.push(selectSessionDataModeCommand());
  return { type: DownloadMachineResultType.CONTINUE, commands, events };
}

function handleWaitingForSessionDataMode(
  downloadState: DownloadState,
  frame: ScpFrame,
  events: Xrs2iEvent[],
  commands: DeviceCommand[],
): DownloadMachineResult {
  if (frame.type !== FrameType.ACK) {
    return { type: DownloadMachineResultType.CONTINUE, commands, events };
  }

  // Ask for the total session count next so progress events can report the full import scope.
  downloadState.step = DownloadStep.WAITING_FOR_SESSION_COUNT;
  commands.push(getSessionCountCommand());
  return { type: DownloadMachineResultType.CONTINUE, commands, events };
}

function handleWaitingForSessionCount(
  downloadState: DownloadState,
  frame: ScpFrame,
  events: Xrs2iEvent[],
  commands: DeviceCommand[],
): DownloadMachineResult {
  if (frame.type !== FrameType.BRACKET) {
    return { type: DownloadMachineResultType.CONTINUE, commands, events };
  }

  let sessionCount: number | null = null;
  if (frame.value !== null) {
    const parsedSessionCount = Number(frame.value);
    if (Number.isInteger(parsedSessionCount) && parsedSessionCount >= 0) {
      downloadState.totalSessionCount = parsedSessionCount;
      sessionCount = parsedSessionCount;
    }
  }

  downloadState.step = DownloadStep.WAITING_FOR_SESSION_ID;
  commands.push(getNextSessionIdCommand());

  if (sessionCount !== null) {
    // Emit an initial progress event as soon as the scanner tells us how many sessions exist.
    events.push({
      type: Xrs2iEventType.SAVED_SCANS_PROGRESS,
      completedSessions: 0,
      totalSessions: sessionCount,
      importedScans: {},
    });
  }

  return { type: DownloadMachineResultType.CONTINUE, commands, events };
}

function handleWaitingForSessionId(
  downloadState: DownloadState,
  frame: ScpFrame,
  events: Xrs2iEvent[],
  commands: DeviceCommand[],
): DownloadMachineResult {
  const deviceValue = toDeviceValue(frame);
  if (deviceValue === undefined) {
    return { type: DownloadMachineResultType.CONTINUE, commands, events };
  }

  if (deviceValue === null) {
    // An empty session ID means the scanner has finished listing sessions, so import can either end or move to selection.
    if (
      downloadState.sessionIds.length === 0 ||
      downloadState.totalSessionCount === 0
    ) {
      return {
        type: DownloadMachineResultType.COMPLETE,
        success: true,
        events,
      };
    }

    downloadState.currentSessionIndex = 0;
    downloadState.step = DownloadStep.WAITING_FOR_SESSION_SELECTION;
    commands.push(
      buildSelectSessionCommand(
        downloadState.sessionIds[downloadState.currentSessionIndex],
      ),
    );
    return { type: DownloadMachineResultType.CONTINUE, commands, events };
  }

  if (!downloadState.sessionsById[deviceValue]) {
    // Record each session ID once so the scanner's order becomes the import order.
    downloadState.sessionIds.push(deviceValue);
    downloadState.sessionsById[deviceValue] = createSavedSession(deviceValue);
  }

  commands.push(getNextSessionIdCommand());
  return { type: DownloadMachineResultType.CONTINUE, commands, events };
}

function handleWaitingForSessionSelection(
  downloadState: DownloadState,
  frame: ScpFrame,
  events: Xrs2iEvent[],
  commands: DeviceCommand[],
): DownloadMachineResult {
  if (frame.type !== FrameType.ACK) {
    return { type: DownloadMachineResultType.CONTINUE, commands, events };
  }

  const sessionId = downloadState.sessionIds[downloadState.currentSessionIndex];
  if (!sessionId) {
    return { type: DownloadMachineResultType.COMPLETE, success: false, events };
  }

  downloadState.step = DownloadStep.WAITING_FOR_SESSION_NAME;
  commands.push(buildSessionNameCommand(sessionId));
  return { type: DownloadMachineResultType.CONTINUE, commands, events };
}

function handleWaitingForSessionName(
  downloadState: DownloadState,
  frame: ScpFrame,
  events: Xrs2iEvent[],
  commands: DeviceCommand[],
): DownloadMachineResult {
  const deviceValue = toDeviceValue(frame);
  if (deviceValue === undefined) {
    return { type: DownloadMachineResultType.CONTINUE, commands, events };
  }

  const session = getCurrentSession(downloadState);
  if (!session) {
    return { type: DownloadMachineResultType.COMPLETE, success: false, events };
  }

  if (deviceValue) {
    session.sessionName = deviceValue;
  }

  // Once the scanner returns the session name, reset the record cursor so record reading starts at the top.
  downloadState.step = DownloadStep.WAITING_FOR_SESSION_RECORD_RESET;
  commands.push(resetSessionRecordCursorCommand());
  return { type: DownloadMachineResultType.CONTINUE, commands, events };
}

function handleWaitingForSessionRecordReset(
  downloadState: DownloadState,
  frame: ScpFrame,
  events: Xrs2iEvent[],
  commands: DeviceCommand[],
): DownloadMachineResult {
  if (frame.type !== FrameType.ACK) {
    return { type: DownloadMachineResultType.CONTINUE, commands, events };
  }

  downloadState.step = DownloadStep.WAITING_FOR_FIELD_CODE;
  commands.push(getNextFieldCodeCommand());
  return { type: DownloadMachineResultType.CONTINUE, commands, events };
}

function handleWaitingForFieldCode(
  downloadState: DownloadState,
  frame: ScpFrame,
  events: Xrs2iEvent[],
  commands: DeviceCommand[],
): DownloadMachineResult {
  const deviceValue = toDeviceValue(frame);
  if (deviceValue === undefined) {
    return { type: DownloadMachineResultType.CONTINUE, commands, events };
  }

  const session = getCurrentSession(downloadState);
  if (!session) {
    return { type: DownloadMachineResultType.COMPLETE, success: false, events };
  }

  if (deviceValue === null) {
    // An empty field code means the scanner is done describing fields and can move into record output.
    downloadState.step = DownloadStep.WAITING_FOR_RECORD;
    commands.push(getNextRecordCommand());
    return { type: DownloadMachineResultType.CONTINUE, commands, events };
  }

  // Field codes are only used to walk the scanner forward, so no per-field state needs to be retained here.
  commands.push(getNextFieldCodeCommand());
  return { type: DownloadMachineResultType.CONTINUE, commands, events };
}

function handleWaitingForRecord(
  downloadState: DownloadState,
  frame: ScpFrame,
  eidOutputFormat: EIDOutputFormat | undefined,
  events: Xrs2iEvent[],
  commands: DeviceCommand[],
): DownloadMachineResult {
  if (frame.type !== FrameType.BRACKET) {
    return { type: DownloadMachineResultType.CONTINUE, commands, events };
  }

  if (frame.value === null) {
    // The blank record frame is the end-of-session marker that triggers progress emission and the next session lookup.
    return advanceSession(downloadState, events, commands);
  }

  return onRecordFrame(
    downloadState,
    frame.value,
    eidOutputFormat,
    events,
    commands,
  );
}

function createSavedSession(sessionId: string): SavedSession {
  // Keep a mutable scan list per session so the import can append rows without rebuilding the session map.
  return {
    sessionId,
    sessionName: undefined,
    scans: [],
  };
}

function getCurrentSession(
  downloadState: DownloadState,
): SavedSession | undefined {
  // Use the session index as the active pointer so the state machine only advances one counter at a time.
  const sessionId = downloadState.sessionIds[downloadState.currentSessionIndex];
  return sessionId ? downloadState.sessionsById[sessionId] : undefined;
}

function toDeviceValue(frame: ScpFrame): string | null | undefined {
  // Normalize bracket and text payload variants into one value shape so reducer branches can ignore transport details.
  if (frame.type === FrameType.BRACKET) {
    return frame.value;
  }

  if (frame.type === FrameType.TEXT) {
    return frame.value || null;
  }

  return undefined;
}

function onRecordFrame(
  downloadState: DownloadState,
  record: string,
  eidOutputFormat: EIDOutputFormat | undefined,
  events: Xrs2iEvent[],
  commands: DeviceCommand[],
): DownloadMachineResult {
  const session = getCurrentSession(downloadState);
  if (!session) {
    return { type: DownloadMachineResultType.COMPLETE, success: false, events };
  }

  const eid = extractEidFromRecord(record, eidOutputFormat);
  if (eid) {
    // Keep only rows that contain a parseable EID so unsupported metadata rows do not pollute the imported scans list.
    session.scans.push({
      EID: eid,
      scanTime: extractScanTime(record),
    });
  }

  commands.push(getNextRecordCommand());
  return { type: DownloadMachineResultType.CONTINUE, commands, events };
}

function advanceSession(
  downloadState: DownloadState,
  events: Xrs2iEvent[],
  commands: DeviceCommand[],
): DownloadMachineResult {
  // Closing record marker means the current saved session is done, so emit progress before moving to the next one.
  const sessionId = downloadState.sessionIds[downloadState.currentSessionIndex];
  const session = sessionId ? downloadState.sessionsById[sessionId] : undefined;
  const completedSessions = downloadState.completedSessionCount + 1;

  events.push({
    type: Xrs2iEventType.SAVED_SCANS_PROGRESS,
    completedSessions,
    totalSessions: downloadState.totalSessionCount,
    importedScans: sessionId
      ? {
          [getSessionLabel(sessionId, session?.sessionName)]:
            session?.scans ?? [],
        }
      : {},
  });

  // Update the completed-session counter before advancing so the next progress event reflects the finished session immediately.
  downloadState.completedSessionCount = completedSessions;
  downloadState.currentSessionIndex += 1;

  if (downloadState.currentSessionIndex >= downloadState.sessionIds.length) {
    return { type: DownloadMachineResultType.COMPLETE, success: true, events };
  }

  downloadState.step = DownloadStep.WAITING_FOR_SESSION_SELECTION;
  commands.push(
    buildSelectSessionCommand(
      downloadState.sessionIds[downloadState.currentSessionIndex],
    ),
  );

  return { type: DownloadMachineResultType.CONTINUE, commands, events };
}

function getSessionLabel(sessionId: string, sessionName?: string): string {
  return sessionName ?? `Session (${sessionId})`;
}
